import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material'
import React from 'react'
import { useTranslation } from '../../../../../common/hooks'
import {
  ReferralForm,
  ReferralModalStep,
  ReferralPatient,
} from '../../types/patientReferrals.types'
import { FormProvider, useForm } from 'react-hook-form'
import { SelectTreatmentStep } from './SelectTreatmentStep'
import { ReferralDetailsEditStep } from './ReferralDetailsEditStep'
import { useUserProfile } from '../../../../../common/hooks/useUserProfile'
import { ReferralDetailsViewStep } from './ReferralDetailsViewStep'
import { useReferralById } from '../../hooks/useReferralById'
import { useReferralCreate } from '../../hooks/useReferralCreate'
import {
  DoctorProfile,
  ReferralStatus,
} from '../../../../../models/graphqlTypes'
import { useNotifications } from '../../../../../common/providers'
import { RouteParams } from '../../../Treatments/types/treatmentPanel.types'
import { useParams } from 'react-router-dom'
import { ReferralStatusChip } from './ReferralStatusChip'

interface ReferralDetailsModalProps {
  isOpen: boolean
  handleModalClose: () => void
  patientProfile: ReferralPatient
  referralId?: string
  refetch: () => void
}

const ReferralDetailsModal: React.FC<
  React.PropsWithChildren<ReferralDetailsModalProps>
> = ({ isOpen, handleModalClose, patientProfile, referralId, refetch }) => {
  const { treatmentId } = useParams<RouteParams>()
  const { t } = useTranslation()
  const { setSuccessToast, setErrorToast } = useNotifications()
  const profile = useUserProfile()

  const { referral, loading } = useReferralById({
    variables: { referralId: referralId ?? '' },
    skip: !referralId,
  })

  const [referralModalStep, setReferralModalStep] = React.useState(
    referralId || treatmentId
      ? ReferralModalStep.ReferralDetails
      : ReferralModalStep.SelectTreatment
  )

  const referralBaseInfo = {
    doctor: {
      title: referral?.doctor.title ?? profile?.title ?? '',
      firstName: referral?.doctor.firstName ?? profile?.firstName ?? '',
      lastName: referral?.doctor.lastName ?? profile?.lastName ?? '',
      stampNumber:
        referral?.doctor.stampNumber ??
        (profile as DoctorProfile).stampNumber ??
        '',
    },
    patient: {
      id: patientProfile.id,
      title: patientProfile.title,
      firstName: patientProfile.firstName,
      lastName: patientProfile.lastName,
      tajNumber: patientProfile.tajNumber,
      birthDate: patientProfile.birthDate,
      addressCity: patientProfile.addressCity,
      addressPostalCode: patientProfile.addressPostalCode,
      addressStreet: patientProfile.addressStreet,
      motherFirstName: patientProfile.motherFirstName,
      motherLastName: patientProfile.motherLastName,
      birthPlace: patientProfile.birthPlace,
    },
  }

  const formMethods = useForm<ReferralForm>({
    defaultValues: {
      treatment: null,
      logNumber: '',
      description: '',
      expirationDate: null,
      profession: null,
      createdAtPlace: '',
      createdAtDate: null,
    },
  })

  const [createReferral] = useReferralCreate()

  const handleNext = () =>
    setReferralModalStep(ReferralModalStep.ReferralDetails)

  const handleSubmit = formMethods.handleSubmit(async (formData) => {
    if (profile?.__typename === 'DoctorProfile') {
      await createReferral({
        variables: {
          referralData: {
            createdAtDate: formData.createdAtDate,
            createdAtPlace: formData.createdAtPlace,
            description: formData.description,
            doctorProfileId: profile.id,
            expirationDate: formData.expirationDate,
            logNumber: formData.logNumber,
            name: 'eBeutaló',
            patientProfileId: patientProfile.id,
            professionId: formData.profession?.id,
            treatmentId: formData.treatment?.id ?? treatmentId ?? '',
            status: ReferralStatus.Created,
          },
        },
        onCompleted: () => {
          refetch()
          setSuccessToast('successCreateReferral')
          handleModalClose()
        },
        onError: (e) => setErrorToast(e),
      })
    }
  })

  if (!referralBaseInfo) {
    return null
  }

  return (
    <Dialog open={isOpen} onClose={handleModalClose} fullWidth>
      <DialogTitle>
        <Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h6">
              {t('doctor:referralDetails.referral')}
            </Typography>
            {referral?.status && (
              <ReferralStatusChip status={referral.status} />
            )}
          </Stack>
          <Typography variant="h6">
            {t('doctor:referralDetails.title')}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <FormProvider {...formMethods}>
          {referralModalStep === ReferralModalStep.SelectTreatment && (
            <SelectTreatmentStep />
          )}
          {referralModalStep === ReferralModalStep.ReferralDetails &&
            (referral ? (
              <ReferralDetailsViewStep
                referralBaseInfo={referralBaseInfo}
                referral={referral}
                loading={loading}
              />
            ) : (
              <ReferralDetailsEditStep referralBaseInfo={referralBaseInfo} />
            ))}
        </FormProvider>
      </DialogContent>

      <DialogActions>
        {referral ? (
          <Button variant="contained" onClick={handleModalClose}>
            {t('common:close')}
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={handleModalClose}>
              {t('common:cancel')}
            </Button>
            {referralModalStep === ReferralModalStep.SelectTreatment && (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!formMethods.watch('treatment')}
              >
                {t('common:continue')}
              </Button>
            )}
            {referralModalStep === ReferralModalStep.ReferralDetails && (
              <Button variant="contained" onClick={handleSubmit}>
                {t('doctor:referralDetails.createButton')}
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export { ReferralDetailsModal }
